import MyCardsCollection from '../../components/MyCardsCollection';
import {
  useAddCardToHaveListMutation,
  useCardToHaveListQuantityUpdateMutation,
  useHaveListFetchPaginatedQuery,
  useRemoveCardFromHaveListMutation,
  useUpdateHaveListCardMutation,
  useDeleteAllHaveListItemsMutation,
  UpdateHaveListCardRequest,
} from 'store/services/collectionList/haveList/haveListApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import './HaveList.css';
import { SortOrder } from 'types/interfaces/sort';
import { CollectionList } from 'types/interfaces/collectionList';
import EditHaveListCardModal from 'components/EditHaveListCardModal';
import DeleteHaveListModal from 'components/DeleteHaveListModal';
import { toast } from 'react-toastify';

const HAVE_LIST_ADD_CARDS_PLACEHOLDER = 'Card name';

export const HaveList = () => {
  const [orderBy, setOrderBy] = useState<{
    sort: string | undefined;
    direction: SortOrder | undefined;
  }>({
    sort: 'created_at',
    direction: 'desc' as SortOrder, // Show newest items first
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'list' | 'comfortable' | 'compact'>(() => {
    // Load saved view mode from localStorage
    const saved = localStorage.getItem('haveListViewMode');
    return (saved as 'list' | 'comfortable' | 'compact') || 'list';
  });
  const [editingCard, setEditingCard] = useState<CollectionList | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search term (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset pagination when search changes
  useEffect(() => {
    if (debouncedSearchTerm) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm]);

  // Clear search on unmount (tab switch)
  useEffect(() => {
    return () => {
      setSearchTerm('');
      setIsSearchOpen(false);
    };
  }, []);

  const {
    data: haveListData,
    isFetching: haveListLoading,
  } = useHaveListFetchPaginatedQuery({
    page: currentPage,
    ...orderBy,
    search: debouncedSearchTerm || undefined, // Don't send empty string
  });

  const [addCard] = useAddCardToHaveListMutation();
  const [removeCard] = useRemoveCardFromHaveListMutation();
  const [haveListCardQuantityChange] = useCardToHaveListQuantityUpdateMutation();
  const [updateCard] = useUpdateHaveListCardMutation();
  const [deleteAllHaveListItems, { isLoading: isDeleting }] = useDeleteAllHaveListItemsMutation();

  const allHaveListCards = useMemo(
    () => haveListData?.haveList || [],
    [haveListData?.haveList]
  );

  const onCardAdd = async (identifier: string | number, cardData?: any) => {
    // For have list, we expect cardId (number) with specific printing
    if (typeof identifier === 'number') {
      try {
        // Determine foil status from card data
        // Backend treats etched cards as foil (foil: true)
        const price = cardData?.price?.[0] || cardData?.price;
        const hasNonFoil = price?.usd != null && Number(price.usd) > 0;
        const hasFoil = price?.usdFoil != null && Number(price.usdFoil) > 0;
        const hasEtched = price?.usdEtched != null && Number(price.usdEtched) > 0;

        // If card has non-foil available, default to non-foil
        // Otherwise if foil or etched exists, add as foil
        const shouldBeFoil = !hasNonFoil && (hasFoil || hasEtched);

        await addCard({
          cardId: identifier,
          quantity: 1,
          condition: 'NEAR_MINT' as any,
          foil: shouldBeFoil,
        }).unwrap();
      } catch (error) {
        console.error('Failed to add card to have list:', error);
        toast.error('Failed to add card. Please try again.');
      }
    } else {
      console.error('Have list requires cardId (number), but received:', typeof identifier);
    }
  };

  const onCardRemove = (identifier: string | number) => {
    // For have list, identifier is the row.id (UserMTGCardHave record ID)
    // The API expects haveListRecordId (the UserMTGCardHave.id)
    if (typeof identifier === 'number') {
      removeCard({ haveListRecordId: identifier, ...orderBy });
    } else {
      console.error('Invalid identifier for have list remove:', identifier);
    }
  };

  const onHaveCardQuantityChange = async (identifier: string | number, change: number) => {
    console.log('onHaveCardQuantityChange called:', { identifier, change, orderBy });

    if (typeof identifier === 'number') {
      try {
        // identifier is the UserMTGCardHave record ID (row.id)
        // The backend expects this as haveListRecordId
        await haveListCardQuantityChange({ haveListRecordId: identifier, change, ...orderBy }).unwrap();
        console.log('Quantity update successful');
      } catch (error) {
        console.error('Quantity update failed:', error);
        toast.error('Failed to update quantity. Please try again.');
      }
    } else {
      console.error('Invalid identifier type for have list quantity change:', typeof identifier);
    }
  };

  const onHeaderSortPress = (key: string, explicitDirection?: SortOrder) => {
    if (explicitDirection) {
      setOrderBy({ sort: key, direction: explicitDirection });
    } else if (key === orderBy.sort) {
      setOrderBy((prev) => ({
        sort: key,
        direction: prev.direction === 'asc' ? 'desc' : 'asc',
      }));
    } else {
      setOrderBy({
        direction: 'asc',
        sort: key,
      });
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
    // Scroll to top of cards container so user sees the new page content
    setTimeout(() => {
      document.querySelector('.collection-cards-container')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 0);
  }, []);

  const handleCardEdit = (card: CollectionList) => {
    console.log('Opening edit modal for card:', card);
    setEditingCard(card);
  };

  const handleSaveEdit = async (updates: UpdateHaveListCardRequest) => {
    try {
      await updateCard(updates).unwrap();
      toast.success('Card updated successfully');
      setEditingCard(null);
    } catch (error) {
      console.error('Failed to update card:', error);
      toast.error('Failed to update card. Please try again.');
      throw error; // Re-throw so modal can handle it
    }
  };

  const handleDeleteAll = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const result = await deleteAllHaveListItems().unwrap();
      toast.success(`Successfully deleted ${result.deletedCount} cards from your collection`);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Failed to delete have list:', error);
      toast.error('Failed to delete collection. Please try again.');
    }
  };

  const handleViewModeChange = useCallback((mode: 'list' | 'comfortable' | 'compact') => {
    setViewMode(mode);
    // Save preference to localStorage
    localStorage.setItem('haveListViewMode', mode);
  }, []);

  // Calculate total pages based on 100 records per page
  const totalPages = haveListData ? Math.ceil(haveListData.totalRecords / 100) : 1;

  return (
    <div className="have-list">
      <MyCardsCollection
        isLoading={haveListLoading}
        data={allHaveListCards}
        order={orderBy.direction}
        orderBy={orderBy.sort}
        addCardsPlaceholder={HAVE_LIST_ADD_CARDS_PLACEHOLDER}
        addCardsTitle="Add cards to your Have List"
        addCardsSubtitle="These are cards in your collection. We'll use this list to auto-match you with other traders on the send page."
        onHeaderSortPress={onHeaderSortPress}
        onCardAdd={onCardAdd}
        cardQuantityChange={onHaveCardQuantityChange}
        onCardDelete={onCardRemove}
        onCardEdit={handleCardEdit}
        useOracleId={false}
        isHaveList={true}
        onDeleteAll={handleDeleteAll}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={haveListData?.totalCards || 0}
        totalRecords={haveListData?.totalRecords || 0}
        onPageChange={handlePageChange}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        isSearchOpen={isSearchOpen}
        onSearchToggle={() => setIsSearchOpen(!isSearchOpen)}
        filteredCount={haveListData?.filteredCount}
      />

      <EditHaveListCardModal
        isOpen={!!editingCard}
        card={editingCard}
        onClose={() => setEditingCard(null)}
        onSave={handleSaveEdit}
      />

      <DeleteHaveListModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};
